// Section 21 comparison tags, derived from thresholds in config.ledger. A tag is a plain label
// for the end screen and for sharing (seed plus tags). Norm tags count a norm established at any
// point in the run, not only at the end (G-05).
import type { Config } from '../../config';
import { mean } from '../../core/math';
import type { GameState, LedgerTag, NormId, Origin } from '../../core/types';
import type { EarlyDeaths } from './facts';

export interface TagFacts {
  /** Deaths other than of old age per season lived here, by origin (M7, G-04). */
  earlyDeaths: Partial<Record<Origin, EarlyDeaths>>;
  /** Newcomers who lived `ledger.foundersClubMinSeasons` or more in the settlement (M7). */
  settledNewcomers: number;
  finalLiteracy: number;
}

export function normEverEstablished(state: GameState, normId: NormId): boolean {
  return (
    state.norms[normId].established ||
    state.ledger.normEvents.some((e) => e.normId === normId && e.change === 'established')
  );
}

export function arrivalShares(state: GameState): {
  arrivals: number;
  admitted: number;
  turnedAway: number;
} {
  const arrivals = state.ledger.arrivals.length;
  const turnedAway = state.ledger.arrivals.filter((a) => a.decision === 'turn-away').length;
  return { arrivals, admitted: arrivals - turnedAway, turnedAway };
}

export function ledgerTags(state: GameState, cfg: Config, facts: TagFacts): LedgerTag[] {
  const l = cfg.ledger;
  const acc = state.ledger;
  const played = state.turn > 0;
  const out: LedgerTag[] = [];
  if (state.series.gini.length > 0 && mean(state.series.gini) < l.egalitarianGini)
    out.push('egalitarian');
  const starvation = acc.deaths.some((d) => d.cause === 'starvation');
  if (normEverEstablished(state, 'workers-first') && !starvation) out.push('utilitarian');
  if (played && acc.personSeasons.child.short === 0 && acc.childLabourSeasons === 0)
    out.push('protective');
  if (played && acc.seedEaten.length === 0 && acc.festivals.length === 0) out.push('austere');
  const gate = arrivalShares(state);
  if (gate.arrivals > 0 && gate.admitted / gate.arrivals >= l.openGateShare) out.push('open-gate');
  if (gate.arrivals > 0 && gate.turnedAway / gate.arrivals >= l.closedGateShare)
    out.push('closed-gate');
  if (normEverEstablished(state, 'harsh-justice')) out.push('hard-hand');
  if (normEverEstablished(state, 'merciful-justice')) out.push('forgiving');
  // "Newcomer life expectancy 10%+ below founders'": life expectancy runs inversely to the rate
  // of early (not old-age) deaths per season lived here, so newcomers must die early at
  // 1 / (1 - gap) of the founders' rate or more, with enough settled newcomers to judge and
  // enough deaths beyond the founders' rate that a death or two of chance does not decide (M7).
  const { founder, newcomer } = facts.earlyDeaths;
  if (
    founder !== undefined &&
    newcomer !== undefined &&
    facts.settledNewcomers >= l.foundersClubMinNewcomers &&
    newcomer.deaths - founder.rate * newcomer.seasons >= l.foundersClubMinExcessDeaths &&
    newcomer.rate * (1 - l.foundersClubGap) >= founder.rate
  )
    out.push('founders-club');
  if (played && facts.finalLiteracy >= l.learnedLiteracy) out.push('learned');
  return out;
}
